// Noise filter for the Sentry browser SDK.
//
// Google Tag Manager, the tags GTM injects and the Meta pixel are loaded from
// scripts we do not ship (see components/misc/CookieConsent.tsx). When a
// browser blocks them, fails to fetch them, or runs them half-loaded, the
// exception still reaches our global error handlers and arrives in Sentry
// looking like an application error. None of it is actionable — the fix would
// have to happen in someone else's script.
//
// The rule for adding anything here: drop an event only when its stack trace
// contains no frame we could edit. Filtering by message is deliberately
// avoided, because the browser reports a blocked GTM fetch and a failed order
// submit with the very same "Failed to fetch" / "Load failed" text, and the
// second one we very much want to see.

const THIRD_PARTY_HOSTS = [
  "googletagmanager.com",
  "google-analytics.com",
  "googleadservices.com",
  "googlesyndication.com",
  "googletagservices.com",
  "doubleclick.net",
  "connect.facebook.net",
  "facebook.net",
];

// Extensions inject scripts into the page and throw from them constantly.
const EXTENSION_PROTOCOLS = [
  "chrome-extension://",
  "moz-extension://",
  "safari-extension://",
  "safari-web-extension://",
  "webkit-masked-url:",
];

// Some extensions do not throw from behind the protocols above. Wallets in
// particular add their provider to the page as a <script> tag, so the frame
// arrives stripped of the extension protocol and looks first-party — the event
// that prompted this list was "app:///evmAsk.js", one wallet extension failing
// to redefine window.ethereum after another had already claimed it. They are
// matched by file name, which is safe only because no file in this repo is or
// could be called any of these. Keep it that way when adding entries.
const INJECTED_SCRIPTS = [
  "evmAsk.js",
  "inpage.js",
  "injectedScript.js",
  // Facebook's in-app browser (FB_IAB, where most of our ad traffic lands)
  // injects its own performance logger and posts the results to the native app
  // over an Android JavaScript bridge. Once that bridge is released the posts
  // throw "Java object is gone" — inside Facebook's script, never inside ours.
  "navigation_performance_logger_android",
];

const escape = (literal) => literal.replace(/\./g, "\\.");

/** Matched against the top stack frame by the SDK's inbound filters. */
export const THIRD_PARTY_URLS = [
  ...THIRD_PARTY_HOSTS.map((host) => new RegExp(escape(host))),
  ...EXTENSION_PROTOCOLS.map((protocol) => new RegExp(`^${protocol}`)),
  ...INJECTED_SCRIPTS.map((name) => new RegExp(`/${escape(name)}(?:[?#]|$)`)),
];

/**
 * Messages that carry no usable stack trace, so the origin check below cannot
 * classify them. All of them come from outside the app.
 */
export const IGNORED_ERRORS = [
  // A cross-origin script threw. The browser withholds file, line and message,
  // so there is nothing to act on — this is the usual shape of a blocked or
  // partially loaded analytics script.
  "Script error.",
  // The GTM/pixel globals are missing because an ad blocker removed the script
  // before our call sites ran.
  /(?:can't find variable: |ReferenceError: )?(?:gtag|fbq|ga|_fbq|dataLayer) is not defined/i,
  /can't find variable: (?:gtag|fbq|ga|_fbq|dataLayer)/i,
  // Outlook's link scanner, not a real browser.
  "Object Not Found Matters Not",
  // Benign layout notification browsers emit; never a real failure.
  /^ResizeObserver loop/,
  // Android WebView wording for "the Java object behind this JS bridge has
  // been released", thrown when an in-app browser (Facebook's, above all)
  // tears its webview down while its own injected script is still posting to
  // it. This one is safe to match on text — the site owns no JS/Java bridge,
  // so the string cannot come from anywhere but a host app's own code.
  /Java object is gone/,
  /Java exception was raised during method invocation/,
];

const isThirdPartyFrame = (frame) => {
  const file = frame.abs_path || frame.filename || "";
  // Injected inline/eval'd code carries no file we could open.
  if (!file || file === "<anonymous>") return true;
  const path = file.split(/[?#]/)[0];
  return (
    // The SDK's own wrappers (setTimeout, addEventListener, fetch) sit on the
    // stack of everything they instrument, third-party throws included. They
    // are not frames we could edit, so they must not keep an event alive on
    // their own — an application frame will still be there when the fault is
    // ours.
    file.includes("@sentry/") ||
    THIRD_PARTY_HOSTS.some((host) => file.includes(host)) ||
    EXTENSION_PROTOCOLS.some((protocol) => file.startsWith(protocol)) ||
    INJECTED_SCRIPTS.some((name) => path.endsWith(`/${name}`))
  );
};

/**
 * Catch-all for third-party errors whose top frame happens to be ours, or
 * whose frames are all extension/inline code. Runs after the URL and message
 * filters above.
 */
export function dropThirdPartyNoise(event) {
  // Anything our own code reported on purpose carries a source tag (order
  // submits, email delivery, price calculation). Those are kept unconditionally
  // — the message may look like third-party noise, the context never is.
  if (event.tags?.source) return event;

  const frames = (event.exception?.values ?? []).flatMap(
    (value) => value.stacktrace?.frames ?? [],
  );

  // No stack at all (captureMessage, or a message filter already handled it).
  if (frames.length === 0) return event;

  return frames.every(isThirdPartyFrame) ? null : event;
}
